/**
 * Sprint 26.0 — the Sub-tasks section (the Plan tree, one level).
 *
 * Lists this task's children (tasks whose parent_task_id === this task), supplied
 * by get_task().subtasks. The whole row navigates to that sub-task's detail.
 * Hidden entirely when there are none.
 */

import type { CSSProperties } from "react";
import { useNavigate } from "react-router";
import type { Task } from "../../../lib/types";

export interface SubtasksSectionProps {
  subtasks: Task[];
}

export function SubtasksSection({ subtasks }: SubtasksSectionProps) {
  const navigate = useNavigate();
  if (subtasks.length === 0) return null;

  return (
    <section style={{ marginBottom: 16 }} data-testid="subtasks-section">
      <div style={label}>Sub-tasks · {subtasks.length}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {subtasks.map((t) => (
          <SubtaskRow key={t.id} task={t} onOpen={() => navigate(`/tasks/${t.id}`)} />
        ))}
      </div>
    </section>
  );
}

function SubtaskRow({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const subtitle = [task.status, task.workflow_state_label].filter(Boolean).join(" · ");
  return (
    <button type="button" style={row} onClick={onOpen}>
      <i className="ti ti-subtask" style={{ fontSize: 15, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{task.title}</span>
        {subtitle && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{subtitle}</span>}
      </span>
      <i className="ti ti-chevron-right" style={{ fontSize: 13, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
    </button>
  );
}

const label: CSSProperties = { fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 6 };

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "9px 11px",
  background: "transparent",
  cursor: "pointer",
};
